import { forwardRef, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  InputAdornment,
  Tooltip,
  IconButton,
  Box,
  Stack,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import {
  setDialog,
  resetForm,
  PointOfSales,
} from "../../../app/slices/posSlice";
import { Controller, useForm } from "react-hook-form";
import { SelectGroup } from "../../../components/Selectors/SelectGroup";
import { SelectProvince } from "../../../components/Selectors/SelectProvince";
import { useState } from "react";
import { SelectRepresentative } from "../../../components/Selectors/SelectRepresentative";
import { NumericFormat } from "react-number-format";
import { SelectCity } from "../../../components/Selectors/SelectCity";
import {
  setCityTable,
  setSelectedProvince,
} from "../../../app/slices/provincesSlice";
import { IoLocation, IoClose } from "react-icons/io5";
import { SelectPosType } from "../../../components/Selectors/SelectPosType";
import { SelectPosField } from "../../../components/Selectors/SelectPosField";
import { SelectJGroup } from "../../../components/Selectors/jomla/SelectJGroup";
import { SelectJRep } from "../../../components/Selectors/SelectJRep";
import Permission from "../../../components/userPermission/userPermission";
import {
  HiOutlineBuildingStorefront,
  HiOutlineUserCircle,
  HiOutlineIdentification,
  HiOutlineRectangleGroup,
  HiOutlineMapPin,
  HiOutlineTag,
  HiOutlineBanknotes,
  HiOutlineBell,
  HiOutlineCog6Tooth,
} from "react-icons/hi2";

// Brand palette (Dejlah primary blue)
const BLUE = "#0C86B6";
const BLUE_DARK = "#0a6b92";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// A titled, bordered group that visually separates each part of the form.
const SectionCard = ({ icon, title, children }) => (
  <Box
    sx={{
      bgcolor: "#ffffff",
      border: "1px solid #edf0f3",
      borderRadius: 3,
      p: { xs: 2, sm: 2.5 },
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(12,134,182,0.12)",
          color: BLUE,
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f2a37" }}>
        {title}
      </Typography>
    </Stack>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

// A boolean setting rendered as a full-width toggle row for easier scanning.
const SwitchCard = ({ field, label, onChange }) => (
  <Box
    sx={{
      border: "1px solid #edf0f3",
      borderRadius: 2,
      px: 2,
      py: 0.25,
      bgcolor: "#fbfcfd",
      height: "100%",
      display: "flex",
      alignItems: "center",
      transition: "border-color .15s ease",
      "&:hover": { borderColor: "rgba(12,134,182,0.5)" },
    }}
  >
    <FormControlLabel
      control={
        <Switch
          {...field}
          checked={!!field.value}
          onChange={onChange ?? field.onChange}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: BLUE },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: BLUE,
            },
          }}
        />
      }
      label={label}
      labelPlacement="start"
      sx={{
        ml: 0,
        mr: 0,
        width: "100%",
        justifyContent: "space-between",
        "& .MuiFormControlLabel-label": { fontSize: 14, color: "#344054" },
      }}
    />
  </Box>
);

const PosForm = (props) => {
  const _PointOfsales = useSelector((state) => state.pointOfsales.pos);
  const _SearchBody = useSelector((state) => state.pointOfsales.searchBody);
  const _Province = useSelector((state) => state.provinces.provinces);
  const _Group = useSelector((state) => state.group.groupsList);
  const _User = useSelector((state) => state.user.userInfo);
  const _PosType = useSelector((state) => state.posType.posTypes.data);
  const _PosField = useSelector((state) => state.posField.posFields.data);
  const _JGroup = useSelector((state) => state.jGroup.jGroupList);
  const _JRep = useSelector((state) => state.user.jRepList.data);

  const [canTransfer, setCanTransfer] = useState(
    _PointOfsales.form.can_transfer || false,
  );
  const isAdd = _PointOfsales.form.id ? false : true;
  const {
    formState: { errors },
    control,
    watch,
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: _PointOfsales.form,
  });

  const isRepresentative = _User.Roles[0].name === "representative";

  const NumericFormatCustom = forwardRef(
    function NumericFormatCustom(props, ref) {
      const { onChange, ...other } = props;

      return (
        <NumericFormat
          {...other}
          getInputRef={ref}
          onValueChange={(values) => {
            onChange({
              target: {
                name: props.name,
                value: values.value,
              },
            });
          }}
          thousandSeparator
          valueIsNumericString
        />
      );
    },
  );

  const onSubmit = (data) => {
    if (isAdd) {
      dispatch(
        PointOfSales.create(
          {
            user_name: data.user_name,
            password: data.password,
            email: data.email,
            mobile: data.mobile,
            name: data.name,
            address: data.address,
            is_active: data.is_active,
            owner: data.owner,
            group_id: data.group_id.id,
            can_transfer: data.can_transfer,
            province_id: data.province_id.id,
            wage: canTransfer ? data.wage : 1,
            max_daily_transfer: canTransfer ? data.max_daily_transfer : 0,
            max_monthly_transfer: canTransfer ? data.max_monthly_transfer : 0,
            max_debt: data.max_debt,
            max_balance: data.max_balance,
            min_balance: data.min_balance,
            city_id: data.city_id.id,
            rep_id: data.rep_id?.id ?? null,
            e_payment_rep_id: data.e_payment_rep_id?.id ?? null,
            j_rep_id: data.j_rep_id ?? null,
            daily_transfer_balance: data.daily_transfer_balance,
            is_approved: data.is_approved ? 1 : 0,
            latitude: data.latitude,
            longitude: data.longitude,
            has_card_issuing: data.has_card_issuing,
            has_external_orders: data.has_external_orders,
            has_e_payment: data.has_e_payment,
            sim_card_number: data.sim_card_number,
            pos_type_id: data.pos_type_id,
            pos_field_id: data.pos_field_id,
            j_group_id: data.j_group_id,
            other_id: data.other_id ?? "",
            lock_after_payment: data.lock_after_payment,
          },
          _SearchBody,
          true,
          props.inNeedReload,
        ),
      );
    } else {
      dispatch(
        PointOfSales.update(
          data.id,
          {
            name: data.name,
            owner: data.owner,
            mobile: data.mobile,
            group_id: data.group_id.id,
            can_transfer: data.can_transfer,
            province_id: data.province_id.id,
            address: data.address,
            wage: data.wage,
            max_daily_transfer: data.max_daily_transfer,
            max_monthly_transfer: data.max_monthly_transfer,
            user_name: data.user_name,
            max_balance: data.max_balance,
            max_debt: data.max_debt,
            min_balance: data.min_balance,
            e_payment_rep_id: data.e_payment_rep_id?.id ?? null,
            j_rep_id: data.j_rep_id ?? null,
            city_id: data.city_id.id,
            daily_transfer_balance: data.daily_transfer_balance,
            is_approved: data.is_approved ? 1 : 0,
            latitude: data.latitude,
            longitude: data.longitude,
            has_card_recharge: data.has_card_recharge,
            has_card_issuing: data.has_card_issuing,
            has_external_orders: data.has_external_orders,
            has_e_payment: data.has_e_payment,
            sim_card_number: data.sim_card_number,
            pos_type_id: data.pos_type_id,
            pos_field_id: data.pos_field_id,
            j_group_id: data.j_group_id,
            other_id: data.other_id ?? "",
            lock_after_payment: data.lock_after_payment,
            rep_id: data.rep_id?.id ?? null,
          },
          _SearchBody,
          props.inNeedReload,
          props.from,
          props.jOrderSearchBody,
          props.jOrderId,
        ),
      );
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (canTransfer === false) {
      setValue("max_monthly_transfer", 0);
      setValue("max_daily_transfer", 0);
      setValue("wage", 0);
    } else {
      setValue("max_monthly_transfer", 5000000);
      setValue("max_daily_transfer", 1000000);
      setValue("wage", 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canTransfer]);

  useEffect(() => {
    const currentJGroup = watch("j_group_id");
    if (
      !isRepresentative &&
      (currentJGroup === null || currentJGroup === undefined) &&
      _JGroup.length > 0
    ) {
      setValue("j_group_id", _JGroup[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_JGroup]);

  const dispatch = useDispatch();

  const handleDialog = () => {
    dispatch(setDialog());
  };

  useEffect(() => {
    const selectedCities = _Province.data?.filter((x) =>
      x.id === watch("province_id")?.id ? x.city : null,
    )[0];
    dispatch(setCityTable(selectedCities?.city));
    dispatch(
      setSelectedProvince({
        id: watch("province_id") ? watch("province_id").id : null,
        name: watch("province_id") ? watch("province_id").name : "",
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_Province, watch("province_id")]);

  useEffect(() => {
    if (watch("group_id") == null) setValue("group_id", _Group?.data[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_Province]);

  return (
    <Dialog
      open={true}
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      maxWidth="md"
      dir="rtl"
      onClose={handleDialog}
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "94vh",
          overflow: "hidden",
          "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BLUE} 0%, #13a2d4 100%)`,
          px: { xs: 2.5, sm: 3 },
          py: 2.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "14px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#fff",
              fontSize: 26,
              flexShrink: 0,
            }}
          >
            <HiOutlineBuildingStorefront />
          </Box>
          <Box>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 20,
                lineHeight: 1.2,
              }}
            >
              {isAdd ? "إضافة نقطة بيع جديدة" : "تعديل نقطة البيع"}
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.82)", fontSize: 13, mt: 0.25 }}
            >
              {isAdd
                ? "أدخل معلومات نقطة البيع لإنشائها"
                : "قم بتحديث معلومات نقطة البيع ثم احفظ التغييرات"}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={handleDialog}
          sx={{
            color: "#fff",
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
          }}
        >
          <IoClose />
        </IconButton>
      </Box>

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <DialogContent sx={{ bgcolor: "#f4f6f8", p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2.5}>
            {/* Account (add mode only) */}
            {isAdd && (
              <SectionCard
                icon={<HiOutlineUserCircle />}
                title="معلومات الحساب"
              >
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="user_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="اسم المستخدم"
                        fullWidth
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="كلمة المرور"
                        fullWidth
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        size="small"
                        label="البريد الالكتروني"
                        fullWidth
                        {...field}
                      />
                    )}
                  />
                </Grid>
              </SectionCard>
            )}

            {/* Basic info */}
            <SectionCard
              icon={<HiOutlineIdentification />}
              title="المعلومات الأساسية"
            >
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      required
                      size="small"
                      label="الاسم"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="other_id"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="معرف النقطة"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="owner"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      required
                      size="small"
                      label="المالك"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="mobile"
                  rules={{
                    required: { value: true, message: "الحقل مطلوب" },
                    pattern: {
                      value: /^(?:\+964|00964|07[5798])[0-9]{8}$/,
                      message: "رقم الهاتف غير صحيح",
                    },
                  }}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      label="رقم الهاتف"
                      fullWidth
                      error={!!errors.mobile}
                      helperText={errors.mobile ? errors.mobile.message : ""}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="sim_card_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="رقم الشريحة"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
            </SectionCard>

            {/* Classification & groups */}
            {!isRepresentative && (
              <SectionCard
                icon={<HiOutlineRectangleGroup />}
                title="التصنيف والمجاميع"
              >
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="rep_id"
                    control={control}
                    render={({ field }) => (
                      <SelectRepresentative
                        value={field.value}
                        onChange={(e, newValue) => {
                          setValue("rep_id", newValue ? newValue : null);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="e_payment_rep_id"
                    control={control}
                    render={({ field }) => (
                      <SelectRepresentative
                        label="مندوب الدفع الالكتروني"
                        value={field.value}
                        onChange={(e, newValue) => {
                          setValue(
                            "e_payment_rep_id",
                            newValue ? newValue : null,
                          );
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="j_rep_id"
                    control={control}
                    render={({ field }) => (
                      <SelectJRep
                        onChange={(e, newValue) => {
                          setValue("j_rep_id", newValue ? newValue.id : null);
                        }}
                        value={
                          _JRep.filter((x) => x.id === field.value)[0] ?? null
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="group_id"
                    control={control}
                    render={({ field }) => (
                      <SelectGroup
                        required
                        value={field.value}
                        onChange={(e, newValue) => {
                          setValue("group_id", newValue);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="j_group_id"
                    control={control}
                    render={({ field }) => (
                      <SelectJGroup
                        required
                        onChange={(e, newValue) => {
                          setValue(
                            "j_group_id",
                            newValue ? newValue?.id : null,
                          );
                        }}
                        value={
                          _JGroup.filter((x) => x.id === field.value)[0] ?? null
                        }
                      />
                    )}
                  />
                </Grid>
              </SectionCard>
            )}

            {/* Location */}
            <SectionCard icon={<HiOutlineMapPin />} title="الموقع">
              <Grid item xs={12} sm={6}>
                <Controller
                  name="province_id"
                  control={control}
                  render={({ field }) => (
                    <SelectProvince
                      required
                      value={field.value}
                      onChange={(e, newValue) => {
                        setValue("province_id", newValue ? newValue : null);
                        setValue("city_id", null);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="city_id"
                  control={control}
                  render={({ field }) => (
                    <SelectCity
                      {...field}
                      required
                      value={field.value}
                      onChange={(e, newValue) => {
                        setValue("city_id", newValue ? newValue : null);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="اقرب نقطة دالة"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="latitude"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="خط العرض (Latitude)"
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="longitude"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      size="small"
                      label="خط الطول (Longitude)"
                      fullWidth
                      {...field}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="عرض الموقع على الخريطة">
                              <span>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  sx={{ color: BLUE }}
                                  disabled={
                                    !watch("longitude") || !watch("latitude")
                                  }
                                  onClick={() => {
                                    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${watch("latitude")},${watch("longitude")}`;
                                    window.open(googleMapsUrl, "_blank");
                                  }}
                                >
                                  <IoLocation />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </SectionCard>

            {/* POS type */}
            <SectionCard icon={<HiOutlineTag />} title="نوع النقطة">
              <Grid item xs={12} sm={6}>
                <Controller
                  name="pos_type_id"
                  control={control}
                  render={({ field }) => (
                    <SelectPosType
                      onChange={(e, newValue) => {
                        setValue("pos_type_id", newValue ? newValue.id : null);
                      }}
                      value={
                        _PosType.filter((x) => x.id === field.value)[0] ?? null
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="pos_field_id"
                  control={control}
                  render={({ field }) => (
                    <SelectPosField
                      onChange={(e, newValue) => {
                        setValue("pos_field_id", newValue ? newValue.id : null);
                      }}
                      value={
                        _PosField.filter((x) => x.id === field.value)[0] ?? null
                      }
                    />
                  )}
                />
              </Grid>
            </SectionCard>

            {/* Financial limits */}
            <SectionCard icon={<HiOutlineBanknotes />} title="الحدود المالية">
              <Grid item xs={12} sm={6}>
                <Controller
                  name="daily_transfer_balance"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      required
                      size="small"
                      label="تحويل الرصيد في اليوم"
                      fullWidth
                      {...field}
                      InputProps={{
                        inputComponent: NumericFormatCustom,
                        startAdornment: (
                          <InputAdornment position="start">IQD</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              {!isRepresentative && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="max_balance"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الاعلى للرصيد"
                        fullWidth
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              {!isRepresentative && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="max_debt"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الاعلى للدين"
                        fullWidth
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
            </SectionCard>

            {/* Balance alerts */}
            {!isRepresentative && (
              <SectionCard icon={<HiOutlineBell />} title="تنبيهات الرصيد">
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="min_balance"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الادنى للرصيد"
                        fullWidth
                        {...field}
                        helperText="للتنبيه فقط - لا يؤثر على عمل النقطة"
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </SectionCard>
            )}

            {/* Features & permissions */}
            <SectionCard
              icon={<HiOutlineCog6Tooth />}
              title="الميزات والصلاحيات"
            >
              <Grid item xs={12} sm={6}>
                <Controller
                  name="has_card_issuing"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="اصدار البطاقات" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="has_external_orders"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="الفواتير الخارجية" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="has_e_payment"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="الدفع الالكتروني" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="lock_after_payment"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="قفل بعد الدفع" />
                  )}
                />
              </Grid>
              {!isAdd && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="has_card_recharge"
                    control={control}
                    render={({ field }) => (
                      <SwitchCard
                        field={field}
                        label="شحن البطاقات"
                        onChange={(e) => {
                          setValue("has_card_recharge", e.target.checked);
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="is_approved"
                  control={control}
                  render={({ field }) => (
                    <SwitchCard field={field} label="المصادقه" />
                  )}
                />
              </Grid>
              {!isRepresentative && (
                <Grid item xs={12}>
                  <Controller
                    name="can_transfer"
                    control={control}
                    render={({ field }) => (
                      <SwitchCard
                        field={field}
                        label="امكانية التحويل"
                        onChange={(e) => {
                          setCanTransfer(e.target.checked);
                          setValue("can_transfer", e.target.checked);
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              {canTransfer && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="max_daily_transfer"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الاعلى للتحويل في اليوم"
                        fullWidth
                        value={field.value}
                        onChange={(e) =>
                          setValue("max_daily_transfer", e.target.value)
                        }
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              {canTransfer && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="max_monthly_transfer"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="الحد الاعلى للتحويل في الشهر"
                        fullWidth
                        value={field.value}
                        onChange={(e) =>
                          setValue("max_monthly_transfer", e.target.value)
                        }
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                          startAdornment: (
                            <InputAdornment position="start">
                              IQD
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              {canTransfer && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="wage"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        required
                        size="small"
                        label="العمولة"
                        fullWidth
                        {...field}
                        InputProps={{
                          inputComponent: NumericFormatCustom,
                        }}
                        helperText="1 = 0.001"
                      />
                    )}
                  />
                </Grid>
              )}
            </SectionCard>
          </Stack>
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            bgcolor: "#ffffff",
            borderTop: "1px solid #edf0f3",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={handleDialog}
            variant="outlined"
            sx={{
              color: "#475467",
              borderColor: "#d0d5dd",
              px: 3,
              "&:hover": { borderColor: "#98a2b3", bgcolor: "#f9fafb" },
            }}
          >
            إلغاء
          </Button>
          <Permission permission="pos_edit" role={["super_admin", "admin"]}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={_PointOfsales.loading}
              sx={{
                bgcolor: BLUE,
                px: 4,
                boxShadow: "0 4px 12px rgba(12,134,182,0.3)",
                "&:hover": { bgcolor: BLUE_DARK },
              }}
            >
              حفظ
            </LoadingButton>
          </Permission>
        </Box>
      </form>
    </Dialog>
  );
};

export default PosForm;
